// src/components/FullGallery.js
import React, { useState, useRef, useEffect } from "react";
import "./FullGallery.css";
import Lightbox from "./Lightbox";
import galleryHeader from "../assets/images/gallery-header.jpg";
import aspectRatios from "../data/galleryAspects.json";

// Bundle every gallery image. We keep each image's source key so we can derive
// meaningful alt text from descriptive filenames (e.g. "CNats 2025 - Mario").
const ctx = require.context(
  "../assets/images/gallery",
  false,
  /\.(png|jpe?g|svg)$/i
);

// Opaque names carry no caption value (UUID exports, or camera/phone originals
// like IMG_1482, DSC_0001, PXL_2024..., screenshots, bare datestamps). These
// render no caption rather than a meaningless filename; only human-named files
// become a caption.
const OPAQUE_PATTERNS = [
  /^[0-9a-f]{8}-[0-9a-f]{4}/i, // UUID exports
  /^IMG[-_ ]?\d/i, // IMG_1482
  /^DSCN?[-_ ]?\d/i, // DSC_0001 / DSCN0001
  /^PXL[-_ ]?\d/i, // Pixel
  /^GOPR\d/i, // GoPro
  /^MVIMG[-_ ]?\d/i, // motion photo
  /^P\d{6,}/i, // P1010101
  /^screenshot/i, // screenshots
  /^\d{6,}/, // bare numeric / 20240101_123456 datestamps
];
const isOpaque = (label) => OPAQUE_PATTERNS.some((re) => re.test(label));

const captionFromKey = (name) => {
  const label = name
    .replace(/\.[a-z]+$/i, "")
    .replace(/_Original$/i, "")
    .replace(/\s+/g, " ")
    .trim();
  return isOpaque(label) ? "" : label;
};

// Ratios come from src/data/galleryAspects.json (built by `npm run gen:gallery-aspects`).
// A photo missing from the manifest falls back to a neutral landscape ratio so a
// stale manifest never breaks the row packing.
const DEFAULT_ASPECT = 1.5;
const images = ctx.keys().map((key, index) => {
  const name = key.replace(/^\.\//, "");
  const caption = captionFromKey(name);
  return {
    key: name,
    index,
    src: ctx(key),
    aspect: aspectRatios[name] || DEFAULT_ASPECT,
    caption,
    alt: caption
      ? `SSS Powerlifting: ${caption}`
      : "SSS Powerlifting athlete competing",
  };
});

const GAP = 12; // px between tiles, kept in sync between the packer and the styles
const rowTargetFor = (width) => (width < 600 ? 150 : width < 1024 ? 200 : 260);

// Justified ("flickr-style") packing: greedily fill each row at the target height,
// then scale the row's height so its tiles span the full width with no cropping.
// The trailing partial row keeps the target height, left-aligned.
const packRows = (items, containerWidth, target, gap) => {
  if (!containerWidth) return [];
  const rows = [];
  let row = [];
  let aspectSum = 0;
  for (const item of items) {
    row.push(item);
    aspectSum += item.aspect;
    const gaps = gap * (row.length - 1);
    if (aspectSum * target + gaps >= containerWidth) {
      rows.push({ items: row, height: (containerWidth - gaps) / aspectSum });
      row = [];
      aspectSum = 0;
    }
  }
  if (row.length) rows.push({ items: row, height: target, last: true });
  return rows;
};

const FullGallery = () => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  // Index of the photo open in the lightbox; null when the viewer is closed.
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const rows = packRows(images, width, rowTargetFor(width), GAP);

  return (
    <div className="full-gallery">
      <header
        className="gallery-hero"
        style={{ backgroundImage: `url(${galleryHeader})` }}
      >
        <div className="gallery-hero__scrim" />
        <div className="gallery-hero__inner">
          <p className="gallery-hero__eyebrow">The gallery</p>
          <h1 className="gallery-hero__title">Moments and memories</h1>
          <p className="gallery-hero__subtitle">
            Meet days, podium finishes, and personal records from across the SSS
            Powerlifting crew.
          </p>
        </div>
      </header>

      <section className="gallery-section">
        <div className="justified-gallery" ref={containerRef}>
          {rows.map((row, i) => (
            <div
              key={i}
              className="justified-row"
              style={{
                height: `${row.height}px`,
                gap: `${GAP}px`,
                marginBottom: i < rows.length - 1 ? `${GAP}px` : 0,
              }}
            >
              {row.items.map((item) => (
                <figure
                  key={item.key}
                  className="justified-item"
                  style={{ width: `${item.aspect * row.height}px` }}
                >
                  <button
                    type="button"
                    className="justified-item__btn"
                    onClick={() => setActiveIndex(item.index)}
                    aria-label={`View larger: ${item.alt}`}
                  >
                    <img
                      src={item.src}
                      alt={item.alt}
                      className="justified-image"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                  {item.caption && (
                    <figcaption className="justified-item__caption">
                      {item.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          ))}
        </div>
      </section>

      {activeIndex !== null && (
        <Lightbox
          images={images}
          index={activeIndex}
          onClose={() => setActiveIndex(null)}
          onChange={setActiveIndex}
        />
      )}
    </div>
  );
};

export default FullGallery;
